import React from "react";
import { Select, Spin } from "antd";
import type { SelectProps } from "antd";
import { LoaderIcon } from "lucide-react";
import { BaseOptionType, DefaultOptionType } from "antd/es/select";

const { Option } = Select;

interface CustomSelectProps<T, K extends BaseOptionType | DefaultOptionType>
  extends SelectProps<T, K> {
  loading?: boolean;
  loadingText?: string;
  customLoadingIcon?: React.ReactNode;
}

const CustomSelectComponent = <
  T,
  K extends BaseOptionType | DefaultOptionType
>({
  loading = false,
  loadingText = "Loading...",
  customLoadingIcon,
  children,
  dropdownRender,
  ...selectProps
}: CustomSelectProps<T, K>) => {
  // Custom loading spinner
  const LoadingSpinner = () => (
    <div className="flex items-center justify-center py-4 px-3">
      <div className="flex items-center space-x-2">
        {customLoadingIcon || (
          <LoaderIcon className="h-4 w-4 animate-spin text-blue-500" />
        )}
        <span className="text-sm text-gray-500">{loadingText}</span>
      </div>
    </div>
  );

  // Custom dropdown render function
  const customDropdownRender = (menu: React.ReactElement) => {
    if (loading) {
      return <LoadingSpinner />;
    }

    if (dropdownRender) {
      return dropdownRender(menu);
    }

    return menu;
  };

  // Custom spinner indicator
  const spinnerIndicator = customLoadingIcon ? (
    (customLoadingIcon as React.ReactElement)
  ) : (
    <LoaderIcon className="h-3 w-3 animate-spin text-blue-500" />
  );

  return (
    <Select
      {...selectProps}
      dropdownRender={customDropdownRender}
      suffixIcon={
        loading ? (
          <Spin size="small" indicator={spinnerIndicator} />
        ) : (
          selectProps.suffixIcon
        )
      }
    >
      {children}
    </Select>
  );
};

// Create a compound component with Option
export const CustomSelect = Object.assign(CustomSelectComponent, {
  Option: Option,
});
